/**
 * When a positive skill signal must NOT be shown (W6, decisions D7 / D9): the
 * corrections verdict wins over praise. The live chip on a learner bubble and
 * the debrief's "Lo que lograste hoy" apply the same rule, so a sentence never
 * reads "✓ passive voice" next to a correction of its passive.
 *
 * A credit is blocked by a correction of the SAME turn when the correction is
 * an error (any severity but `suggestion`; an unknown severity counts as an
 * error, like the client's fail-safe `structural`) and either
 *
 *   - names the credit's skill (`skillIds`, classified server-side by
 *     `praiseBlockingSkillIds`: the error is about that skill, wherever in the
 *     turn it is), or
 *   - touches the words that earned the credit (`said` and `evidence` overlap:
 *     one contains the other, or they share a run of 2+ words) — this covers
 *     corrections that carry no `skillIds`.
 *
 * Precision first: a block can withhold a deserved chip, never the reverse.
 * The display filters (sensitivity level, corrections toggle) are NOT applied
 * here: a hidden minor error in the passive still withholds the passive chip.
 *
 * Also here: which of a session's skill credits the debrief lists
 * (`debriefListsSkillCredit`), read by the client's list and the server's
 * "is this summary rich" alike.
 */

export interface PraiseCandidate {
  skillId: string;
  /** The learner's words that earned the credit (a verbatim span of the turn). */
  evidence: string;
}

export interface PraiseBlockingCorrection {
  said: string;
  severity?: 'communication-breaking' | 'structural' | 'minor' | 'suggestion' | null | undefined;
  skillIds?: ReadonlyArray<string> | undefined;
}

/** Words of a span, for overlap: lowercase, straight apostrophes, punctuation out. */
export function praiseWords(text: string): string[] {
  return (
    text
      .toLowerCase()
      .replace(/[‘’ʼ]/g, "'")
      .match(/[\p{L}\p{N}]+(?:'[\p{L}\p{N}]+)*/gu) ?? []
  );
}

function containsRun(outer: readonly string[], inner: readonly string[]): boolean {
  if (inner.length === 0 || inner.length > outer.length) return false;
  for (let i = 0; i + inner.length <= outer.length; i += 1) {
    let ok = true;
    for (let j = 0; j < inner.length; j += 1) {
      if (outer[i + j] !== inner[j]) {
        ok = false;
        break;
      }
    }
    if (ok) return true;
  }
  return false;
}

/** Longest run of consecutive words two spans share. */
function longestSharedRun(a: readonly string[], b: readonly string[]): number {
  let best = 0;
  for (let i = 0; i < a.length; i += 1) {
    for (let j = 0; j < b.length; j += 1) {
      let k = 0;
      while (i + k < a.length && j + k < b.length && a[i + k] === b[j + k]) k += 1;
      if (k > best) best = k;
    }
  }
  return best;
}

/** The two spans of the same turn touch: one contains the other, or they share 2+ consecutive words. */
export function praiseSpansOverlap(evidence: string, said: string): boolean {
  const ev = praiseWords(evidence);
  const sd = praiseWords(said);
  if (ev.length === 0 || sd.length === 0) return false;
  if (containsRun(ev, sd) || containsRun(sd, ev)) return true;
  return longestSharedRun(ev, sd) >= 2;
}

export function correctionBlocksPraise(
  credit: PraiseCandidate,
  correction: PraiseBlockingCorrection,
): boolean {
  if (correction.severity === 'suggestion') return false;
  if (correction.skillIds?.includes(credit.skillId)) return true;
  return praiseSpansOverlap(credit.evidence, correction.said);
}

/** Some correction of the turn blocks this credit's praise. */
export function skillPraiseBlocked(
  credit: PraiseCandidate,
  corrections: ReadonlyArray<PraiseBlockingCorrection>,
): boolean {
  return corrections.some((c) => correctionBlocksPraise(credit, c));
}

/**
 * Whether the session's errors take "lo clavaste" (D9) away from a credit. A
 * radar focus is not nailed in a session that also has an error of it, before
 * or after the correct use, in any turn — the praise would sit next to the
 * error (E2E 2026-10-07: "lo clavaste" beside «racha 0 de 2»). Only the
 * focus's OWN skill can nail it, so an error naming the credit's skill
 * (`skillIds`, `praiseBlockingSkillIds` server-side) is an error of the focus.
 * Suggestions are not errors. The credit itself stands: only the nail goes.
 *
 * Live, the server applies it to the corrections already persisted when the
 * credit is recorded and the client to the ones that land later in the
 * thread; the debrief applies it to the whole session (plus the radar's own
 * attribution of the session's errors).
 */
export function nailRevokedBySession(
  skillId: string,
  sessionCorrections: ReadonlyArray<Pick<PraiseBlockingCorrection, 'severity' | 'skillIds'>>,
): boolean {
  return sessionCorrections.some(
    (c) => c.severity !== 'suggestion' && (c.skillIds?.includes(skillId) ?? false),
  );
}

/** The fields of a session's skill credit (debrief summary) the listing rule reads. */
export interface DebriefSkillCreditFlags {
  /** The catalog celebrates the skill (D7); `false` = a silent credit. */
  celebrate: boolean;
  /** Who credited it this session (judge / lexicon / lesson / session). */
  sources: ReadonlyArray<string>;
  /** Reps this session moved. */
  gained: number;
  completedNow: boolean;
  completedBefore: boolean;
  nailedFocus: { focusId: string } | null;
}

/**
 * Credited by the lexicon matcher this session: vocabulary, "progreso en
 * silencio, sin festejo" (owner, 2026-10-07) — no live chip, but the debrief
 * lists its progress.
 */
export function isSilentVocabularyCredit(c: Pick<DebriefSkillCreditFlags, 'sources'>): boolean {
  return c.sources.includes('lexicon');
}

/**
 * Credited by the session-end judge (`source: 'session'` — a story, an
 * opinion defended, a summary… over several turns): it lands after the
 * session, so it never had a live chip; the debrief lists its progress
 * (owner: progress of the skills without a chip shows in "Lo que lograste
 * hoy"), as LATE data — once the analysis settles.
 */
export function isSessionEndCredit(c: Pick<DebriefSkillCreditFlags, 'sources'>): boolean {
  return c.sources.includes('session');
}

/**
 * Whether the debrief's "Lo que lograste hoy" lists a session's skill credit
 * (W6): the session completed a celebrated skill, the credit nailed a radar
 * focus (any skill, D9), or a celebrated skill — or a vocabulary skill of the
 * lexicon matcher, or a skill of the session-end judge — moved and was not
 * complete before. Any other silent credit is listed only through a nailed
 * focus.
 *
 * Shared because the server reads it too: only a credit the debrief will show
 * makes a summary "rich" — one it never lists must not turn a short session's
 * note into an empty layout.
 */
export function debriefListsSkillCredit(c: DebriefSkillCreditFlags): boolean {
  if (c.completedNow && c.celebrate) return true;
  if (c.nailedFocus !== null) return true;
  return (
    (c.celebrate || isSilentVocabularyCredit(c) || isSessionEndCredit(c)) && c.gained > 0 && !c.completedBefore
  );
}
